import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { parseAsInteger, parseAsStringEnum, useQueryStates } from 'nuqs';
import { ChargeZemItem, CountryCode, Currency } from '@zep/types';
import { TossPaymentMethod } from '../../../../consts';
import { fCurrency } from '../../../../utils';
import { getZemChargeCurrency } from './utils/getZemChargeCurrency';
import { getSelectedZemChargeOption, getZemChargeOptions } from './utils/getZemChargeOptions';
export enum ChargeZemStep {
  Zem = 'zem',
  Payment = 'payment',
}
export type ZemQueryStringState = {
  step: ChargeZemStep;
  paymentMethod: TossPaymentMethod;
  zem: number;
  handleStep: (step: ChargeZemStep) => void;
  handleZem: (zem: number) => void;
  handlePaymentMethod: (paymentMethod1: TossPaymentMethod) => void;
};
type _ChargeZemItem = ChargeZemItem & {
  price: number;
};
export type ZemChargeState = {
  currency: Currency;
  enabledPaymentButton: boolean;
};
type ZemChargeContextType = ZemChargeState & ZemQueryStringState & {
  setPaymentButtonState: (newState: ZemChargeContextType['enabledPaymentButton']) => void;
  zemItems: _ChargeZemItem[];
  selectedZemInfo: _ChargeZemItem & {
    localeString: string;
    productName: string;
  };
};
const initialZemChargeState: ZemChargeState = {
  currency: 'KRW',
  enabledPaymentButton: false
};
const ZemChargeContext = createContext<ZemChargeContextType | undefined>(undefined);

// Provider 컴포넌트 생성
interface ChargeZemProviderProps {
  children: ReactNode;
  countyCode: CountryCode;
  initZemItems: ChargeZemItem[];
}
export function ChargeZemProvider({
  children,
  countyCode,
  initZemItems
}: ChargeZemProviderProps) {
  const [zemChargeState, setZemChargeState] = useState<ZemChargeState>({
    ...initialZemChargeState,
    currency: getZemChargeCurrency(countyCode)
  });
  const zemItems = getZemChargeOptions(initZemItems).map(item => ({
    ...item,
    price: zemChargeState.currency === 'KRW' ? item.krwPrice : zemChargeState.currency === 'JPY' ? item.jpyPrice : item.usdPrice
  }));
  if (zemItems.length === 0) {
    throw new Error('사용 가능한 ZEM 충전 상품이 없습니다.');
  }
  const [queryStringState, setQueryStringState] = useQueryStates({
    step: parseAsStringEnum<ChargeZemStep>(Object.values(ChargeZemStep)).withDefault(ChargeZemStep.Zem),
    zem: parseAsInteger.withDefault(zemItems[0].addCashMoney),
    paymentMethod: parseAsStringEnum<TossPaymentMethod>(Object.values(TossPaymentMethod)).withDefault(TossPaymentMethod.Card)
  });
  const handleStep = (step: ChargeZemStep) => {
    setQueryStringState(prev => ({
      ...prev,
      paymentMethod: null,
      step
    }));
  };
  const handlePaymentMethod = (paymentMethod: TossPaymentMethod) => {
    setQueryStringState(prev => ({
      ...prev,
      paymentMethod
    }));
  };
  const setPaymentButtonState = (_state: ZemChargeState['enabledPaymentButton']) => {
    setZemChargeState(prevState => ({
      ...prevState,
      enabledPaymentButton: _state
    }));
  };
  const handleZem = (zem: number) => {
    setQueryStringState(() => ({
      step: null,
      paymentMethod: null,
      zem
    }));
  };
  const selectedZemInfo = (() => {
    const origin = getSelectedZemChargeOption(zemItems, queryStringState.zem);
    return {
      ...origin,
      productName: origin.name,
      localeString: fCurrency(origin.price, zemChargeState.currency)
    };
  })();
  useEffect(() => {
    if (queryStringState.step === 'zem') {
      setPaymentButtonState(true);
      return;
    }
    if (queryStringState.step === 'payment') {
      setPaymentButtonState(false);
    }
  }, [queryStringState.step]);
  return <ZemChargeContext.Provider value={{
    enabledPaymentButton: zemChargeState.enabledPaymentButton,
    currency: zemChargeState.currency,
    step: queryStringState.step,
    paymentMethod: queryStringState.paymentMethod,
    zem: selectedZemInfo.addCashMoney,
    handleZem,
    handleStep,
    handlePaymentMethod,
    setPaymentButtonState,
    zemItems,
    selectedZemInfo
  }} data-sentry-element="unknown" data-sentry-component="ChargeZemProvider" data-sentry-source-file="ZemChargeContext.tsx">
      {children}
    </ZemChargeContext.Provider>;
}

// 커스텀 훅 생성
export function useChargeZem() {
  const context = useContext(ZemChargeContext);
  if (!context) {
    throw new Error('useZemCharge must be used within a ZemChargeProvider');
  }
  return context;
}